<script setup lang="ts">
/**
 * More information about why this component behave like this
 * - https://pencilandpaper.io/articles/ux-pattern-analysis-loading-feedback/#loading-patterns
 *
 * (TL;DR) Core principles
 * - Always give immediate feedback right after receiving the request
 * - For waits longer than 2 seconds, use a looped animation (indeterminate)
 * - For waits longer than 10 seconds, use a percent-done indicator (determinate)
 *
 * In this component we use only the IMMEDIATE and INDETERMINATE waits
 *
 * The icons used are by https://potlabicons.com/
 */
import type { Component, Ref } from 'vue';
import { ref, reactive, computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  loaderAvailableNames,
  loaderStepName,
} from '@shared/types/ui-loader';
import { UILoader } from '@shared/types/ui-loader';

const { t } = useI18n();

interface loaderAvailableType {
  name: loaderAvailableNames;
}

interface loaderProps {
  immediateMessage?: string;
  loader?: loaderAvailableNames;
  indeterminateMessage?: string[];
}

interface loadingStepIndicatorType {
  defaultMessage: string;
  messages?: string[];
}

const props = withDefaults(defineProps<loaderProps>(), {
  loader: 'DEFAULT',
  immediateMessage: '',
  indeterminateMessage: () => [''],
});

const loaderAvailable: loaderAvailableType[] = [
  {
    name: UILoader.DEFAULT,
  },
  {
    name: UILoader.UPLOAD,
  },
  {
    name: UILoader.MINIMALIST,
  },
];

const currentLoader: loaderAvailableType[] = loaderAvailable.filter(
  (loader) => props.loader === loader.name,
);

const loaderModules: Record<string, () => Promise<Component>> =
  import.meta.glob('./icon/*Loader.vue');

const currentLoaderComponent = computed(() => {
  const name = currentLoader[0].name.toLocaleLowerCase();
  const path = `./icon/${name}Loader.vue`;

  return defineAsyncComponent(
    loaderModules[path] ?? loaderModules['./icon/defaultLoader.vue'],
  );
});

const loadingStepIndicatorMap: Record<
  loaderStepName,
  loadingStepIndicatorType
> = reactive({
  IMMEDIATE: {
    defaultMessage: props.immediateMessage,
  },
  INDETERMINATE: {
    defaultMessage: props.indeterminateMessage[0],
    messages: props.indeterminateMessage,
  },
  DEFAULT: {
    defaultMessage: t('app.loader_text.default'),
  },
});

let loadingState: Ref<UILoader> = ref(UILoader.IMMEDIATE);

const immediateWaitId = setInterval(
  changeLoadingState,
  100,
  UILoader.IMMEDIATE,
);

const indeterminateWaitId = setInterval(
  changeLoadingState,
  2000,
  UILoader.INDETERMINATE,
);

// If we don't have any custom message we fallback to the default message and we clear all the interval
if (props.immediateMessage === '' || props.indeterminateMessage[0] === '') {
  loadingState.value = UILoader.DEFAULT;
  clearInterval(immediateWaitId);
  clearInterval(indeterminateWaitId);
}

function changeLoadingState(state: UILoader) {
  switch (state) {
    case UILoader.IMMEDIATE:
      clearInterval(immediateWaitId);
      break;
    case UILoader.INDETERMINATE:
      clearInterval(indeterminateWaitId);
      changeInderminateMessage();
      break;
    case UILoader.DEFAULT:
      break;
  }
  loadingState.value = state;
}

/**
 * Change the loading message every 2000ms
 */
function changeInderminateMessage() {
  const messages = loadingStepIndicatorMap.INDETERMINATE.messages ?? [];
  for (let i = 0; i < messages.length; i++) {
    setTimeout(() => {
      loadingStepIndicatorMap.INDETERMINATE.defaultMessage = messages[i];
    }, i * 2000);
  }
}
</script>

<template>
  <div class="loader-wrapper">
    <slot name="icon-load">
      <component :is="currentLoaderComponent" />
    </slot>
    <div v-if="loadingState === UILoader.IMMEDIATE">
      {{ loadingStepIndicatorMap.IMMEDIATE.defaultMessage }}
    </div>
    <div v-else-if="loadingState === UILoader.INDETERMINATE">
      {{ loadingStepIndicatorMap.INDETERMINATE.defaultMessage }}
    </div>
    <div v-else-if="currentLoader[0].name !== UILoader.MINIMALIST">
      {{ loadingStepIndicatorMap.DEFAULT.defaultMessage }}
    </div>
  </div>
</template>

<style lang="postcss" scoped src="./UILoader.pcss"></style>
